// Investor pitch deck translations. Proper nouns (GreenBelt, BMV Global, B3,
// UCS, ISIN, SGS, TÜV Rheinland, EY, ERM, KSA, SBTi, Article 6.4, MiCA, FATF)
// intentionally left untranslated. Prices, numbers and currency codes are
// interpolated at render time and never localised.

const pitch = {
  en: {
    top_label: "GreenBelt · Investor Deck",
    pdf_download: "PDF ↓",
    exit: "Exit",
    prev: "← Prev",
    next: "Next →",

    // Slide 1 — title
    s1: {
      eyebrow: "Investor deck · confidential",
      title_a: "Trading the",
      title_em: "regulated carbon",
      title_b: "asset of Brazil.",
      body_pre: "GreenBelt is the exchange for the ",
      body_em: "Unidade de Crédito de Sustentabilidade",
      body_post: " — a Brazilian ecological asset backed by B3 ISINs, Law 13.986/2020 (CPR Verde), and the BMV Global methodology.",
      hero_stat: "528,000 + ha",
      hero_caption: "Real forest under conservation — verified by BMV Global.",
      per_tco2: "per tCO₂",
      spot_brl: "Spot · BRL",
      spot_usd: "Spot · USD",
      spot_eur: "Spot · EUR",
    },

    // Slide 2 — problem
    s2: {
      eyebrow: "The problem",
      h2_a: "Voluntary carbon markets are",
      h2_credible: "credible",
      h2_broken: "broken",
      h2_dot: ".",
      body_pre: "The $2B voluntary carbon market is fragmented across ",
      body_em1: "25+ private registries",
      body_mid: " with no price discovery, no settlement standard, and a 2023 Verra-credibility crisis that erased ",
      body_em2: "90% of certain rainforest credits",
      body_post: ".",
      pain1_t: "Buyers can't verify what they buy.",
      pain1_s: "no public ISIN, no central registry",
      pain2_t: "Sellers lose 70% to middlemen.",
      pain2_s: "broker / aggregator stacking",
      pain3_t: "Corporates face legal exposure.",
      pain3_s: "post-Verra scandal liability",
      pain4_t: "Cross-border settlement is opaque.",
      pain4_s: "no FX standard, manual wires",
    },

    // Slide 3 — solution
    s3: {
      eyebrow: "The solution",
      h2_a: "A regulated exchange.",
      h2_em: "Public registry.",
      h2_b: "Real settlement.",
      p1_h: "Regulated asset",
      p1_b: "UCS is a CPR Verde registered with B3 under Brazilian Law 13.986/2020. Every unit has an ISIN code, every issuance is traceable.",
      p2_h: "Public verification",
      p2_b: "Our /registry page lets anyone confirm unit balances directly at B3 — using only the ISIN and issuer CNPJ.",
      p3_h: "Real settlement",
      p3_b: "Stripe + SWIFT + USDT, multi-currency wallet (USD / EUR / BRL / GBP / SAR), corporate KYB built-in.",
    },

    // Slide 4 — product
    s4: {
      eyebrow: "What we built",
      h2_a: "A complete UCS exchange —",
      h2_em: "already live",
      h2_b: ".",
      f1_t: "Marketplace",
      f1_s: "7 BMV-audited projects · real-time UCS Index pricing",
      f2_t: "Public B3 ISIN registry",
      f2_s: "Downloadable signed PDF for compliance teams",
      f3_t: "Multi-currency wallet",
      f3_s: "USD · EUR · BRL · GBP · SAR settlement",
      f4_t: "Mitigation flow",
      f4_s: "Issue retirement certificates (self / platform attribution)",
      f5_t: "Corporate KYB",
      f5_s: "VIES VAT lookup · EU + OFAC sanctions screening",
      f6_t: "Quote workflow",
      f6_s: "Auto < $500k · concierge desk > $500k",
      f7_t: "5 languages · RTL",
      f7_s: "EN · PT · ES · FR · AR",
      f8_t: "Email pipeline",
      f8_s: "Resend · admin alerts + buyer receipts",
    },

    // Slide 5 — differentiators
    s5: {
      eyebrow: "Differentiators",
      h2_a: "Five things only",
      h2_em: "GreenBelt",
      h2_b: "has.",
      d1_t: "Regulated",
      d1_s: "UCS is a CPR Verde under Brazilian Law 13.986/2020.",
      d2_t: "B3 ISIN",
      d2_s: "Every unit publicly verifiable on B3.",
      d3_t: "Tier-1 audit",
      d3_s: "SGS · TÜV Rheinland · EY · ERM.",
      d4_t: "Multi-currency",
      d4_s: "Stripe · SWIFT · USDT, buyer chooses.",
      d5_t: "Public verification",
      d5_s: "B3 lookup in 30 seconds.",
    },

    // Slide 6 — live network
    s6: {
      eyebrow: "Live network",
      h2_a: "Our network,",
      h2_em: "today",
      h2_b: ".",
      stat1_l: "Projects live",
      stat1_s: "all BMV-audited",
      stat2_l: "Hectares preserved",
      stat2_s: "across 3 biomes",
      stat3_l: "tCO₂ available",
      stat3_s: "tradeable inventory",
      stat4_l: "Avg spot",
      stat4_s: "per tCO₂ · USD",
      audit_label: "Audit network",
      audit_val: "SGS · TÜV Rheinland · Ernst & Young · ERM",
      biomes_label: "Biomes",
      biomes_val: "Amazon · Cerrado · Atlantic Forest",
      producer_label: "Producer",
      producer_val: "BMV Agro (BMV Global)",
    },

    // Slide 7 — market
    s7: {
      eyebrow: "Market",
      h2_a: "From",
      h2_today: "$2B today",
      h2_to: "to",
      h2_2030: "$50B by 2030",
      h2_dot: ".",
      body_pre: "The voluntary carbon market is forecast to grow ",
      body_em: "25× by 2030",
      body_post: " as Article 6.4 of the Paris Agreement enters force and corporate net-zero commitments hit their first hard deadline (2030 SBTi).",
      stat1_l: "Current VCM",
      stat1_s: "Ecosystem Marketplace 2024",
      stat2_l: "2030 projected",
      stat2_s: "McKinsey · BCG · TSVCM",
      stat3_l: "Brazil share",
      stat3_s: "REDD+ + Cerrado conservation",
      stat4_l: "Transparency",
      stat4_s: "price discovery today",
      pull: "Brazil holds the world's most defensible carbon stock. GreenBelt is the regulated, ISIN-anchored on-ramp.",
    },

    // Slide 8 — business model
    s8: {
      eyebrow: "Business model",
      h2_a: "Three revenue lines,",
      h2_em: "one platform",
      h2_b: ".",
      r1_l: "Transaction fee",
      r1_s: "On every primary sale, buyer-side, embedded in spot price.",
      r2_l: "FX spread",
      r2_s: "Cross-currency settlement across USD · EUR · BRL · GBP · SAR.",
      r3_l: "Concierge desk",
      r3_s: "Custom quote + onboarding for orders > $500k.",
      future_label: "future revenue:",
      future_val: "corporate auto-mitigation subscriptions · custody / hold-to-vintage retirement accounts · pre-issuance forwards.",
    },

    // Slide 9 — partners & traction
    s9: {
      eyebrow: "Partners & traction",
      h2_a: "Already",
      h2_em: "in market",
      h2_b: ".",
      strategic_partners: "Strategic partners",
      sp1: "<b>BMV Global</b> — exclusive distribution of the UCS Index ecosystem",
      sp2: "<b>B3</b> — official issuer registry · Resolução nº 31/2022",
      sp3_a: "<b>Ministry of Energy · KSA</b>",
      sp3_note: "— in discussion",
      sp3_b: " · Vision 2030 net-zero alignment",
      sp4: "<b>Audit network</b> — SGS · TÜV Rheinland · EY · ERM",
      platform_traction: "Platform traction",
      t1_a: "Live in production ·",
      t1_url: "credit-marketplace-11.emergent.host",
      t2_stats: "{{count}} projects · {{hectares}} ha · {{tons}} tCO₂",
      t3: "LIVE Stripe + SWIFT + USDT settlement rails",
      t4: "5 languages · 5 currencies · multi-jurisdiction KYB",
      t5: "Resend transactional emails active",
    },

    // Slide 10 — the ask
    s10: {
      eyebrow: "The ask",
      h1_a: "Raising",
      h1_em: "€3M",
      h1_b: "Seed.",
      sub: "To convert a fully-operational platform into the default exchange for the regulated Brazilian voluntary carbon market.",
      a1_t: "Go-to-market",
      a1_s: "Enterprise sales · KSA / EU / UK ESG procurement partnerships",
      a2_t: "Engineering & compliance",
      a2_s: "FATF Travel Rule · MiCA preparation · on-chain settlement",
      a3_t: "Regulatory & strategic",
      a3_s: "Sovereign offtake agreements · audit-firm exclusives",
      contact: "investors@greenbelt.io →",
      demo_cta: "Open live demo →",
    },
  },

  pt: {
    top_label: "GreenBelt · Deck para investidores",
    pdf_download: "PDF ↓",
    exit: "Sair",
    prev: "← Anterior",
    next: "Próximo →",
    s1: {
      eyebrow: "Deck para investidores · confidencial",
      title_a: "Negociando o",
      title_em: "carbono regulado",
      title_b: "do Brasil.",
      body_pre: "A GreenBelt é a bolsa para a ",
      body_em: "Unidade de Crédito de Sustentabilidade",
      body_post: " — um ativo ecológico brasileiro respaldado por ISINs da B3, pela Lei 13.986/2020 (CPR Verde) e pela metodologia BMV Global.",
      hero_stat: "528.000 + ha",
      hero_caption: "Floresta real em conservação — verificada pela BMV Global.",
      per_tco2: "por tCO₂",
      spot_brl: "Spot · BRL",
      spot_usd: "Spot · USD",
      spot_eur: "Spot · EUR",
    },
    s2: {
      eyebrow: "O problema",
      h2_a: "Os mercados voluntários de carbono estão",
      h2_credible: "confiáveis",
      h2_broken: "quebrados",
      h2_dot: ".",
      body_pre: "O mercado voluntário de US$ 2 bi é fragmentado em ",
      body_em1: "mais de 25 registros privados",
      body_mid: " sem descoberta de preço, sem padrão de liquidação e uma crise de credibilidade da Verra em 2023 que apagou ",
      body_em2: "90% de certos créditos florestais",
      body_post: ".",
      pain1_t: "Compradores não conseguem verificar o que compram.",
      pain1_s: "sem ISIN público, sem registro central",
      pain2_t: "Vendedores perdem 70% para intermediários.",
      pain2_s: "empilhamento de corretor / agregador",
      pain3_t: "Corporações enfrentam exposição jurídica.",
      pain3_s: "responsabilidade pós-escândalo Verra",
      pain4_t: "Liquidação transfronteiriça é opaca.",
      pain4_s: "sem padrão de câmbio, transferências manuais",
    },
    s3: {
      eyebrow: "A solução",
      h2_a: "Uma bolsa regulada.",
      h2_em: "Registro público.",
      h2_b: "Liquidação real.",
      p1_h: "Ativo regulado",
      p1_b: "A UCS é uma CPR Verde registrada na B3 pela Lei 13.986/2020. Cada unidade possui código ISIN, cada emissão é rastreável.",
      p2_h: "Verificação pública",
      p2_b: "Nossa página /registry permite que qualquer pessoa confirme saldos de unidades diretamente na B3 — apenas com o ISIN e o CNPJ do emissor.",
      p3_h: "Liquidação real",
      p3_b: "Stripe + SWIFT + USDT, carteira multi-moeda (USD / EUR / BRL / GBP / SAR), KYB corporativo integrado.",
    },
    s4: {
      eyebrow: "O que construímos",
      h2_a: "Uma bolsa UCS completa —",
      h2_em: "já operando",
      h2_b: ".",
      f1_t: "Marketplace",
      f1_s: "7 projetos auditados pela BMV · preços em tempo real do Índice UCS",
      f2_t: "Registro público B3 ISIN",
      f2_s: "PDF assinado para download por equipes de compliance",
      f3_t: "Carteira multi-moeda",
      f3_s: "Liquidação em USD · EUR · BRL · GBP · SAR",
      f4_t: "Fluxo de mitigação",
      f4_s: "Emissão de certificados de aposentadoria (atribuição própria / plataforma)",
      f5_t: "KYB corporativo",
      f5_s: "Consulta VIES VAT · triagem de sanções UE + OFAC",
      f6_t: "Fluxo de cotação",
      f6_s: "Automático < US$ 500 mil · mesa concierge > US$ 500 mil",
      f7_t: "5 idiomas · RTL",
      f7_s: "EN · PT · ES · FR · AR",
      f8_t: "Pipeline de e-mails",
      f8_s: "Resend · alertas admin + recibos de comprador",
    },
    s5: {
      eyebrow: "Diferenciais",
      h2_a: "Cinco coisas que só a",
      h2_em: "GreenBelt",
      h2_b: "tem.",
      d1_t: "Regulado",
      d1_s: "UCS é CPR Verde sob a Lei brasileira 13.986/2020.",
      d2_t: "B3 ISIN",
      d2_s: "Cada unidade verificável publicamente na B3.",
      d3_t: "Auditoria Tier-1",
      d3_s: "SGS · TÜV Rheinland · EY · ERM.",
      d4_t: "Multi-moeda",
      d4_s: "Stripe · SWIFT · USDT, escolha do comprador.",
      d5_t: "Verificação pública",
      d5_s: "Consulta B3 em 30 segundos.",
    },
    s6: {
      eyebrow: "Rede em operação",
      h2_a: "Nossa rede,",
      h2_em: "hoje",
      h2_b: ".",
      stat1_l: "Projetos ativos",
      stat1_s: "todos auditados pela BMV",
      stat2_l: "Hectares preservados",
      stat2_s: "em 3 biomas",
      stat3_l: "tCO₂ disponíveis",
      stat3_s: "estoque negociável",
      stat4_l: "Spot médio",
      stat4_s: "por tCO₂ · USD",
      audit_label: "Rede de auditoria",
      audit_val: "SGS · TÜV Rheinland · Ernst & Young · ERM",
      biomes_label: "Biomas",
      biomes_val: "Amazônia · Cerrado · Mata Atlântica",
      producer_label: "Produtor",
      producer_val: "BMV Agro (BMV Global)",
    },
    s7: {
      eyebrow: "Mercado",
      h2_a: "De",
      h2_today: "US$ 2 bi hoje",
      h2_to: "para",
      h2_2030: "US$ 50 bi até 2030",
      h2_dot: ".",
      body_pre: "Projeta-se que o mercado voluntário de carbono cresça ",
      body_em: "25× até 2030",
      body_post: " com a entrada em vigor do Artigo 6.4 do Acordo de Paris e o primeiro prazo forte dos compromissos corporativos de net-zero (SBTi 2030).",
      stat1_l: "VCM atual",
      stat1_s: "Ecosystem Marketplace 2024",
      stat2_l: "Projeção 2030",
      stat2_s: "McKinsey · BCG · TSVCM",
      stat3_l: "Participação do Brasil",
      stat3_s: "REDD+ + conservação do Cerrado",
      stat4_l: "Transparência",
      stat4_s: "descoberta de preço hoje",
      pull: "O Brasil detém o estoque de carbono mais defensável do mundo. A GreenBelt é a rampa de acesso regulada e ancorada em ISIN.",
    },
    s8: {
      eyebrow: "Modelo de negócio",
      h2_a: "Três linhas de receita,",
      h2_em: "uma plataforma",
      h2_b: ".",
      r1_l: "Taxa de transação",
      r1_s: "Em cada venda primária, lado do comprador, embutida no preço spot.",
      r2_l: "Spread de câmbio",
      r2_s: "Liquidação cross-currency em USD · EUR · BRL · GBP · SAR.",
      r3_l: "Mesa concierge",
      r3_s: "Cotação personalizada + onboarding para pedidos > US$ 500 mil.",
      future_label: "receita futura:",
      future_val: "assinaturas corporativas de auto-mitigação · custódia / contas de aposentadoria por safra · forwards pré-emissão.",
    },
    s9: {
      eyebrow: "Parceiros e tração",
      h2_a: "Já",
      h2_em: "no mercado",
      h2_b: ".",
      strategic_partners: "Parceiros estratégicos",
      sp1: "<b>BMV Global</b> — distribuição exclusiva do ecossistema do Índice UCS",
      sp2: "<b>B3</b> — registro oficial de emissores · Resolução nº 31/2022",
      sp3_a: "<b>Ministério de Energia · KSA</b>",
      sp3_note: "— em negociação",
      sp3_b: " · alinhamento com Vision 2030 net-zero",
      sp4: "<b>Rede de auditoria</b> — SGS · TÜV Rheinland · EY · ERM",
      platform_traction: "Tração da plataforma",
      t1_a: "Em produção ·",
      t1_url: "credit-marketplace-11.emergent.host",
      t2_stats: "{{count}} projetos · {{hectares}} ha · {{tons}} tCO₂",
      t3: "Trilhos de liquidação Stripe + SWIFT + USDT em LIVE",
      t4: "5 idiomas · 5 moedas · KYB multi-jurisdicional",
      t5: "E-mails transacionais Resend ativos",
    },
    s10: {
      eyebrow: "O pedido",
      h1_a: "Captando",
      h1_em: "€3M",
      h1_b: "Seed.",
      sub: "Para transformar uma plataforma plenamente operacional na bolsa padrão do mercado voluntário brasileiro de carbono regulado.",
      a1_t: "Go-to-market",
      a1_s: "Vendas corporativas · parcerias de compras ESG na KSA / UE / UK",
      a2_t: "Engenharia & compliance",
      a2_s: "FATF Travel Rule · preparação MiCA · liquidação on-chain",
      a3_t: "Regulatório & estratégico",
      a3_s: "Contratos soberanos de offtake · exclusividades com firmas de auditoria",
      contact: "investors@greenbelt.io →",
      demo_cta: "Abrir demo ao vivo →",
    },
  },

  es: {
    top_label: "GreenBelt · Pitch para inversores",
    pdf_download: "PDF ↓",
    exit: "Salir",
    prev: "← Anterior",
    next: "Siguiente →",
    s1: {
      eyebrow: "Pitch para inversores · confidencial",
      title_a: "Operando el",
      title_em: "carbono regulado",
      title_b: "de Brasil.",
      body_pre: "GreenBelt es la bolsa para la ",
      body_em: "Unidade de Crédito de Sustentabilidade",
      body_post: " — un activo ecológico brasileño respaldado por ISINs de B3, la Ley 13.986/2020 (CPR Verde) y la metodología BMV Global.",
      hero_stat: "528.000 + ha",
      hero_caption: "Bosque real en conservación — verificado por BMV Global.",
      per_tco2: "por tCO₂",
      spot_brl: "Spot · BRL",
      spot_usd: "Spot · USD",
      spot_eur: "Spot · EUR",
    },
    s2: {
      eyebrow: "El problema",
      h2_a: "Los mercados voluntarios de carbono están",
      h2_credible: "creíbles",
      h2_broken: "rotos",
      h2_dot: ".",
      body_pre: "El mercado voluntario de $2 mil millones está fragmentado en ",
      body_em1: "más de 25 registros privados",
      body_mid: " sin descubrimiento de precios, sin estándar de liquidación, y una crisis de credibilidad de Verra en 2023 que borró el ",
      body_em2: "90% de ciertos créditos forestales",
      body_post: ".",
      pain1_t: "Los compradores no pueden verificar lo que compran.",
      pain1_s: "sin ISIN público, sin registro central",
      pain2_t: "Los vendedores pierden 70% con intermediarios.",
      pain2_s: "apilamiento de broker / agregador",
      pain3_t: "Las corporativas enfrentan exposición legal.",
      pain3_s: "responsabilidad post-escándalo Verra",
      pain4_t: "La liquidación transfronteriza es opaca.",
      pain4_s: "sin estándar de FX, transferencias manuales",
    },
    s3: {
      eyebrow: "La solución",
      h2_a: "Una bolsa regulada.",
      h2_em: "Registro público.",
      h2_b: "Liquidación real.",
      p1_h: "Activo regulado",
      p1_b: "UCS es una CPR Verde registrada en B3 bajo la Ley brasileña 13.986/2020. Cada unidad tiene código ISIN, cada emisión es rastreable.",
      p2_h: "Verificación pública",
      p2_b: "Nuestra página /registry permite a cualquiera confirmar saldos directamente en B3 — solo con el ISIN y el CNPJ del emisor.",
      p3_h: "Liquidación real",
      p3_b: "Stripe + SWIFT + USDT, cartera multi-moneda (USD / EUR / BRL / GBP / SAR), KYB corporativo integrado.",
    },
    s4: {
      eyebrow: "Lo que construimos",
      h2_a: "Una bolsa UCS completa —",
      h2_em: "ya en vivo",
      h2_b: ".",
      f1_t: "Marketplace",
      f1_s: "7 proyectos auditados por BMV · precios en tiempo real del Índice UCS",
      f2_t: "Registro público B3 ISIN",
      f2_s: "PDF firmado descargable para equipos de compliance",
      f3_t: "Cartera multi-moneda",
      f3_s: "Liquidación en USD · EUR · BRL · GBP · SAR",
      f4_t: "Flujo de mitigación",
      f4_s: "Emisión de certificados de retiro (auto / atribución de plataforma)",
      f5_t: "KYB corporativo",
      f5_s: "Consulta VIES VAT · screening de sanciones UE + OFAC",
      f6_t: "Flujo de cotización",
      f6_s: "Automático < $500k · mesa concierge > $500k",
      f7_t: "5 idiomas · RTL",
      f7_s: "EN · PT · ES · FR · AR",
      f8_t: "Pipeline de emails",
      f8_s: "Resend · alertas admin + recibos de comprador",
    },
    s5: {
      eyebrow: "Diferenciadores",
      h2_a: "Cinco cosas que solo",
      h2_em: "GreenBelt",
      h2_b: "tiene.",
      d1_t: "Regulado",
      d1_s: "UCS es CPR Verde bajo la Ley brasileña 13.986/2020.",
      d2_t: "B3 ISIN",
      d2_s: "Cada unidad verificable públicamente en B3.",
      d3_t: "Auditoría Tier-1",
      d3_s: "SGS · TÜV Rheinland · EY · ERM.",
      d4_t: "Multi-moneda",
      d4_s: "Stripe · SWIFT · USDT, elige el comprador.",
      d5_t: "Verificación pública",
      d5_s: "Consulta B3 en 30 segundos.",
    },
    s6: {
      eyebrow: "Red en operación",
      h2_a: "Nuestra red,",
      h2_em: "hoy",
      h2_b: ".",
      stat1_l: "Proyectos activos",
      stat1_s: "todos auditados por BMV",
      stat2_l: "Hectáreas preservadas",
      stat2_s: "en 3 biomas",
      stat3_l: "tCO₂ disponibles",
      stat3_s: "inventario negociable",
      stat4_l: "Spot promedio",
      stat4_s: "por tCO₂ · USD",
      audit_label: "Red de auditoría",
      audit_val: "SGS · TÜV Rheinland · Ernst & Young · ERM",
      biomes_label: "Biomas",
      biomes_val: "Amazonía · Cerrado · Bosque Atlántico",
      producer_label: "Productor",
      producer_val: "BMV Agro (BMV Global)",
    },
    s7: {
      eyebrow: "Mercado",
      h2_a: "De",
      h2_today: "$2 mil M hoy",
      h2_to: "a",
      h2_2030: "$50 mil M para 2030",
      h2_dot: ".",
      body_pre: "Se prevé que el mercado voluntario de carbono crezca ",
      body_em: "25× para 2030",
      body_post: " con la entrada en vigor del Artículo 6.4 del Acuerdo de París y el primer plazo firme de los compromisos corporativos net-zero (SBTi 2030).",
      stat1_l: "VCM actual",
      stat1_s: "Ecosystem Marketplace 2024",
      stat2_l: "Proyección 2030",
      stat2_s: "McKinsey · BCG · TSVCM",
      stat3_l: "Participación de Brasil",
      stat3_s: "REDD+ + conservación del Cerrado",
      stat4_l: "Transparencia",
      stat4_s: "descubrimiento de precios hoy",
      pull: "Brasil tiene el stock de carbono más defendible del mundo. GreenBelt es la rampa regulada y anclada en ISIN.",
    },
    s8: {
      eyebrow: "Modelo de negocio",
      h2_a: "Tres líneas de ingresos,",
      h2_em: "una plataforma",
      h2_b: ".",
      r1_l: "Comisión de transacción",
      r1_s: "En cada venta primaria, lado comprador, embebida en el precio spot.",
      r2_l: "Spread de FX",
      r2_s: "Liquidación cross-currency en USD · EUR · BRL · GBP · SAR.",
      r3_l: "Mesa concierge",
      r3_s: "Cotización personalizada + onboarding para pedidos > $500k.",
      future_label: "ingresos futuros:",
      future_val: "suscripciones corporativas de auto-mitigación · custodia / cuentas de retiro por vintage · forwards pre-emisión.",
    },
    s9: {
      eyebrow: "Socios y tracción",
      h2_a: "Ya",
      h2_em: "en el mercado",
      h2_b: ".",
      strategic_partners: "Socios estratégicos",
      sp1: "<b>BMV Global</b> — distribución exclusiva del ecosistema del Índice UCS",
      sp2: "<b>B3</b> — registro oficial de emisores · Resolução nº 31/2022",
      sp3_a: "<b>Ministerio de Energía · KSA</b>",
      sp3_note: "— en negociación",
      sp3_b: " · alineamiento con Vision 2030 net-zero",
      sp4: "<b>Red de auditoría</b> — SGS · TÜV Rheinland · EY · ERM",
      platform_traction: "Tracción de plataforma",
      t1_a: "En producción ·",
      t1_url: "credit-marketplace-11.emergent.host",
      t2_stats: "{{count}} proyectos · {{hectares}} ha · {{tons}} tCO₂",
      t3: "Rieles de liquidación Stripe + SWIFT + USDT en LIVE",
      t4: "5 idiomas · 5 monedas · KYB multi-jurisdiccional",
      t5: "Emails transaccionales Resend activos",
    },
    s10: {
      eyebrow: "La solicitud",
      h1_a: "Levantando",
      h1_em: "€3M",
      h1_b: "Seed.",
      sub: "Para convertir una plataforma plenamente operativa en la bolsa por defecto del mercado voluntario brasileño de carbono regulado.",
      a1_t: "Go-to-market",
      a1_s: "Ventas corporativas · alianzas de compras ESG en KSA / UE / UK",
      a2_t: "Ingeniería & compliance",
      a2_s: "FATF Travel Rule · preparación MiCA · liquidación on-chain",
      a3_t: "Regulatorio & estratégico",
      a3_s: "Contratos soberanos de offtake · exclusividades con firmas de auditoría",
      contact: "investors@greenbelt.io →",
      demo_cta: "Abrir demo en vivo →",
    },
  },

  fr: {
    top_label: "GreenBelt · Deck investisseurs",
    pdf_download: "PDF ↓",
    exit: "Quitter",
    prev: "← Précédent",
    next: "Suivant →",
    s1: {
      eyebrow: "Deck investisseurs · confidentiel",
      title_a: "Négocier le",
      title_em: "carbone régulé",
      title_b: "du Brésil.",
      body_pre: "GreenBelt est la bourse pour l'",
      body_em: "Unidade de Crédito de Sustentabilidade",
      body_post: " — un actif écologique brésilien adossé aux ISINs B3, à la Loi 13.986/2020 (CPR Verde) et à la méthodologie BMV Global.",
      hero_stat: "528 000 + ha",
      hero_caption: "Forêt réelle sous conservation — vérifiée par BMV Global.",
      per_tco2: "par tCO₂",
      spot_brl: "Spot · BRL",
      spot_usd: "Spot · USD",
      spot_eur: "Spot · EUR",
    },
    s2: {
      eyebrow: "Le problème",
      h2_a: "Les marchés volontaires du carbone sont",
      h2_credible: "crédibles",
      h2_broken: "cassés",
      h2_dot: ".",
      body_pre: "Le marché volontaire de 2 Md$ est fragmenté sur ",
      body_em1: "plus de 25 registres privés",
      body_mid: " sans découverte de prix, sans standard de règlement, et une crise de crédibilité Verra en 2023 qui a effacé ",
      body_em2: "90 % de certains crédits forestiers",
      body_post: ".",
      pain1_t: "Les acheteurs ne peuvent pas vérifier ce qu'ils achètent.",
      pain1_s: "pas d'ISIN public, pas de registre central",
      pain2_t: "Les vendeurs perdent 70 % au profit des intermédiaires.",
      pain2_s: "empilement broker / agrégateur",
      pain3_t: "Les entreprises font face à une exposition juridique.",
      pain3_s: "responsabilité post-scandale Verra",
      pain4_t: "Le règlement transfrontalier est opaque.",
      pain4_s: "pas de standard FX, virements manuels",
    },
    s3: {
      eyebrow: "La solution",
      h2_a: "Une bourse régulée.",
      h2_em: "Registre public.",
      h2_b: "Règlement réel.",
      p1_h: "Actif régulé",
      p1_b: "L'UCS est une CPR Verde enregistrée à B3 sous la Loi brésilienne 13.986/2020. Chaque unité a un code ISIN, chaque émission est traçable.",
      p2_h: "Vérification publique",
      p2_b: "Notre page /registry permet à quiconque de confirmer les soldes directement à B3 — avec seulement l'ISIN et le CNPJ de l'émetteur.",
      p3_h: "Règlement réel",
      p3_b: "Stripe + SWIFT + USDT, portefeuille multi-devises (USD / EUR / BRL / GBP / SAR), KYB entreprise intégré.",
    },
    s4: {
      eyebrow: "Ce que nous avons construit",
      h2_a: "Une bourse UCS complète —",
      h2_em: "déjà en ligne",
      h2_b: ".",
      f1_t: "Marketplace",
      f1_s: "7 projets audités BMV · tarification en temps réel de l'Indice UCS",
      f2_t: "Registre public B3 ISIN",
      f2_s: "PDF signé téléchargeable pour les équipes conformité",
      f3_t: "Portefeuille multi-devises",
      f3_s: "Règlement en USD · EUR · BRL · GBP · SAR",
      f4_t: "Flux de compensation",
      f4_s: "Émission de certificats de retrait (attribution personnelle / plateforme)",
      f5_t: "KYB entreprise",
      f5_s: "Consultation VIES VAT · criblage sanctions UE + OFAC",
      f6_t: "Flux de devis",
      f6_s: "Automatique < 500 k$ · pupitre concierge > 500 k$",
      f7_t: "5 langues · RTL",
      f7_s: "EN · PT · ES · FR · AR",
      f8_t: "Pipeline emails",
      f8_s: "Resend · alertes admin + reçus acheteurs",
    },
    s5: {
      eyebrow: "Différenciateurs",
      h2_a: "Cinq choses que seul",
      h2_em: "GreenBelt",
      h2_b: "possède.",
      d1_t: "Régulé",
      d1_s: "UCS est une CPR Verde sous la Loi brésilienne 13.986/2020.",
      d2_t: "B3 ISIN",
      d2_s: "Chaque unité vérifiable publiquement sur B3.",
      d3_t: "Audit Tier-1",
      d3_s: "SGS · TÜV Rheinland · EY · ERM.",
      d4_t: "Multi-devises",
      d4_s: "Stripe · SWIFT · USDT, choix de l'acheteur.",
      d5_t: "Vérification publique",
      d5_s: "Consultation B3 en 30 secondes.",
    },
    s6: {
      eyebrow: "Réseau en direct",
      h2_a: "Notre réseau,",
      h2_em: "aujourd'hui",
      h2_b: ".",
      stat1_l: "Projets actifs",
      stat1_s: "tous audités BMV",
      stat2_l: "Hectares préservés",
      stat2_s: "à travers 3 biomes",
      stat3_l: "tCO₂ disponibles",
      stat3_s: "inventaire négociable",
      stat4_l: "Spot moyen",
      stat4_s: "par tCO₂ · USD",
      audit_label: "Réseau d'audit",
      audit_val: "SGS · TÜV Rheinland · Ernst & Young · ERM",
      biomes_label: "Biomes",
      biomes_val: "Amazonie · Cerrado · Forêt Atlantique",
      producer_label: "Producteur",
      producer_val: "BMV Agro (BMV Global)",
    },
    s7: {
      eyebrow: "Marché",
      h2_a: "De",
      h2_today: "2 Md$ aujourd'hui",
      h2_to: "à",
      h2_2030: "50 Md$ d'ici 2030",
      h2_dot: ".",
      body_pre: "Le marché volontaire du carbone devrait croître de ",
      body_em: "25× d'ici 2030",
      body_post: " avec l'entrée en vigueur de l'Article 6.4 de l'Accord de Paris et la première échéance ferme des engagements corporate net-zero (SBTi 2030).",
      stat1_l: "VCM actuel",
      stat1_s: "Ecosystem Marketplace 2024",
      stat2_l: "Projection 2030",
      stat2_s: "McKinsey · BCG · TSVCM",
      stat3_l: "Part du Brésil",
      stat3_s: "REDD+ + conservation du Cerrado",
      stat4_l: "Transparence",
      stat4_s: "découverte de prix aujourd'hui",
      pull: "Le Brésil détient le stock de carbone le plus défendable au monde. GreenBelt est la rampe d'accès régulée et ancrée à l'ISIN.",
    },
    s8: {
      eyebrow: "Modèle économique",
      h2_a: "Trois flux de revenus,",
      h2_em: "une plateforme",
      h2_b: ".",
      r1_l: "Commission de transaction",
      r1_s: "Sur chaque vente primaire, côté acheteur, intégrée au prix spot.",
      r2_l: "Spread de change",
      r2_s: "Règlement cross-currency en USD · EUR · BRL · GBP · SAR.",
      r3_l: "Pupitre concierge",
      r3_s: "Devis personnalisé + onboarding pour commandes > 500 k$.",
      future_label: "revenus futurs :",
      future_val: "abonnements corporate d'auto-compensation · custody / comptes de retrait par millésime · forwards pré-émission.",
    },
    s9: {
      eyebrow: "Partenaires et traction",
      h2_a: "Déjà",
      h2_em: "sur le marché",
      h2_b: ".",
      strategic_partners: "Partenaires stratégiques",
      sp1: "<b>BMV Global</b> — distribution exclusive de l'écosystème de l'Indice UCS",
      sp2: "<b>B3</b> — registre officiel émetteurs · Resolução nº 31/2022",
      sp3_a: "<b>Ministère de l'Énergie · KSA</b>",
      sp3_note: "— en discussion",
      sp3_b: " · alignement Vision 2030 net-zero",
      sp4: "<b>Réseau d'audit</b> — SGS · TÜV Rheinland · EY · ERM",
      platform_traction: "Traction plateforme",
      t1_a: "En production ·",
      t1_url: "credit-marketplace-11.emergent.host",
      t2_stats: "{{count}} projets · {{hectares}} ha · {{tons}} tCO₂",
      t3: "Rails de règlement Stripe + SWIFT + USDT en LIVE",
      t4: "5 langues · 5 devises · KYB multi-juridictionnel",
      t5: "Emails transactionnels Resend actifs",
    },
    s10: {
      eyebrow: "La demande",
      h1_a: "Levée de",
      h1_em: "€3M",
      h1_b: "Seed.",
      sub: "Pour transformer une plateforme pleinement opérationnelle en la bourse par défaut du marché brésilien du carbone volontaire régulé.",
      a1_t: "Go-to-market",
      a1_s: "Ventes entreprise · partenariats d'achat ESG KSA / UE / UK",
      a2_t: "Ingénierie & conformité",
      a2_s: "FATF Travel Rule · préparation MiCA · règlement on-chain",
      a3_t: "Réglementaire & stratégique",
      a3_s: "Contrats d'offtake souverains · exclusivités cabinets d'audit",
      contact: "investors@greenbelt.io →",
      demo_cta: "Ouvrir la démo en direct →",
    },
  },

  ar: {
    top_label: "غرين بيلت · عرض المستثمرين",
    pdf_download: "PDF ↓",
    exit: "خروج",
    prev: "→ السابق",
    next: "← التالي",
    s1: {
      eyebrow: "عرض المستثمرين · سرّي",
      title_a: "تداول",
      title_em: "كربون البرازيل",
      title_b: "المنظَّم.",
      body_pre: "غرين بيلت هي بورصة ",
      body_em: "وحدة ائتمان الاستدامة",
      body_post: " — أصل بيئي برازيلي مدعوم برموز ISIN من B3 وقانون 13.986/2020 (CPR Verde) ومنهجية BMV Global.",
      hero_stat: "528,000 + هكتار",
      hero_caption: "غابة حقيقية تحت الحماية — موثَّقة من BMV Global.",
      per_tco2: "لكل طن CO₂",
      spot_brl: "الفوري · BRL",
      spot_usd: "الفوري · USD",
      spot_eur: "الفوري · EUR",
    },
    s2: {
      eyebrow: "المشكلة",
      h2_a: "أسواق الكربون الطوعية",
      h2_credible: "موثوقة",
      h2_broken: "معطَّلة",
      h2_dot: ".",
      body_pre: "سوق الكربون الطوعي البالغ 2 مليار دولار مجزَّأ عبر ",
      body_em1: "أكثر من 25 سجلًا خاصًا",
      body_mid: " دون اكتشاف للسعر، ودون معيار تسوية، وأزمة مصداقية Verra في 2023 محت ",
      body_em2: "90% من بعض أرصدة الغابات المطيرة",
      body_post: ".",
      pain1_t: "المشترون لا يستطيعون التحقّق مما يشترونه.",
      pain1_s: "لا ISIN علني، لا سجل مركزي",
      pain2_t: "البائعون يخسرون 70% للوسطاء.",
      pain2_s: "تراكم الوسطاء والمُجمِّعين",
      pain3_t: "الشركات تواجه انكشافًا قانونيًا.",
      pain3_s: "المسؤولية ما بعد فضيحة Verra",
      pain4_t: "التسوية عبر الحدود غامضة.",
      pain4_s: "لا معيار صرف، تحويلات يدوية",
    },
    s3: {
      eyebrow: "الحل",
      h2_a: "بورصة منظَّمة.",
      h2_em: "سجل علني.",
      h2_b: "تسوية حقيقية.",
      p1_h: "أصل منظَّم",
      p1_b: "UCS هو CPR Verde مسجَّل في B3 بموجب القانون البرازيلي 13.986/2020. كل وحدة لها رمز ISIN، وكل إصدار قابل للتتبع.",
      p2_h: "تحقّق علني",
      p2_b: "تتيح صفحة /registry لأي شخص تأكيد أرصدة الوحدات مباشرة في B3 — باستخدام ISIN وCNPJ الجهة المُصدِرة فقط.",
      p3_h: "تسوية حقيقية",
      p3_b: "Stripe + SWIFT + USDT، محفظة متعدّدة العملات (USD / EUR / BRL / GBP / SAR)، مع KYB شركات مدمج.",
    },
    s4: {
      eyebrow: "ما بنيناه",
      h2_a: "بورصة UCS كاملة —",
      h2_em: "بالفعل قيد التشغيل",
      h2_b: ".",
      f1_t: "السوق",
      f1_s: "7 مشاريع مُدقَّقة من BMV · تسعير فوري لمؤشر UCS",
      f2_t: "سجل B3 ISIN علني",
      f2_s: "PDF موقَّع قابل للتنزيل لفِرَق الامتثال",
      f3_t: "محفظة متعدّدة العملات",
      f3_s: "تسوية بـ USD · EUR · BRL · GBP · SAR",
      f4_t: "تدفق التعويض",
      f4_s: "إصدار شهادات السحب (نسبة ذاتية / منصّة)",
      f5_t: "KYB الشركات",
      f5_s: "بحث VIES VAT · فحص عقوبات الاتحاد الأوروبي + OFAC",
      f6_t: "تدفق العروض",
      f6_s: "تلقائي < 500 ألف $ · مكتب مخصص > 500 ألف $",
      f7_t: "5 لغات · RTL",
      f7_s: "EN · PT · ES · FR · AR",
      f8_t: "قناة البريد",
      f8_s: "Resend · تنبيهات الإدارة + إيصالات المشتري",
    },
    s5: {
      eyebrow: "المميّزات",
      h2_a: "خمسة أشياء تنفرد بها",
      h2_em: "غرين بيلت",
      h2_b: ".",
      d1_t: "منظَّم",
      d1_s: "UCS هو CPR Verde بموجب القانون البرازيلي 13.986/2020.",
      d2_t: "B3 ISIN",
      d2_s: "كل وحدة قابلة للتحقّق علنيًا على B3.",
      d3_t: "تدقيق Tier-1",
      d3_s: "SGS · TÜV Rheinland · EY · ERM.",
      d4_t: "متعدّدة العملات",
      d4_s: "Stripe · SWIFT · USDT، المشتري يختار.",
      d5_t: "تحقّق علني",
      d5_s: "بحث B3 في 30 ثانية.",
    },
    s6: {
      eyebrow: "الشبكة الحيّة",
      h2_a: "شبكتنا،",
      h2_em: "اليوم",
      h2_b: ".",
      stat1_l: "المشاريع النشطة",
      stat1_s: "جميعها مُدقَّقة من BMV",
      stat2_l: "الهكتارات المحميّة",
      stat2_s: "عبر 3 أنظمة بيئية",
      stat3_l: "طن CO₂ متاح",
      stat3_s: "مخزون قابل للتداول",
      stat4_l: "متوسط الفوري",
      stat4_s: "لكل طن CO₂ · USD",
      audit_label: "شبكة التدقيق",
      audit_val: "SGS · TÜV Rheinland · Ernst & Young · ERM",
      biomes_label: "الأنظمة البيئية",
      biomes_val: "الأمازون · السيرادو · الغابات الأطلسية",
      producer_label: "المنتج",
      producer_val: "BMV Agro (BMV Global)",
    },
    s7: {
      eyebrow: "السوق",
      h2_a: "من",
      h2_today: "2 مليار $ اليوم",
      h2_to: "إلى",
      h2_2030: "50 مليار $ بحلول 2030",
      h2_dot: ".",
      body_pre: "من المتوقّع أن ينمو سوق الكربون الطوعي ",
      body_em: "25× بحلول 2030",
      body_post: " مع سريان المادة 6.4 من اتفاقية باريس وأول موعد نهائي صارم للتزامات الحياد الكربوني للشركات (SBTi 2030).",
      stat1_l: "VCM الحالي",
      stat1_s: "Ecosystem Marketplace 2024",
      stat2_l: "توقّعات 2030",
      stat2_s: "McKinsey · BCG · TSVCM",
      stat3_l: "حصة البرازيل",
      stat3_s: "REDD+ + حماية السيرادو",
      stat4_l: "الشفافية",
      stat4_s: "اكتشاف الأسعار اليوم",
      pull: "تمتلك البرازيل أكثر مخزون كربوني قابل للدفاع في العالم. غرين بيلت هي المنصّة المنظَّمة المرتكزة على ISIN.",
    },
    s8: {
      eyebrow: "نموذج العمل",
      h2_a: "ثلاثة مصادر إيراد،",
      h2_em: "منصّة واحدة",
      h2_b: ".",
      r1_l: "رسوم المعاملات",
      r1_s: "على كل بيع أوّلي، من جانب المشتري، مضمَّنة في السعر الفوري.",
      r2_l: "فارق الصرف",
      r2_s: "تسوية عبر العملات USD · EUR · BRL · GBP · SAR.",
      r3_l: "مكتب مخصص",
      r3_s: "عرض مخصّص + إعداد الطلبات فوق 500 ألف $.",
      future_label: "الإيرادات المستقبلية:",
      future_val: "اشتراكات تعويض تلقائي للشركات · حسابات حفظ/سحب بحسب السنة · صفقات آجلة قبل الإصدار.",
    },
    s9: {
      eyebrow: "الشركاء والاعتماد",
      h2_a: "بالفعل",
      h2_em: "في السوق",
      h2_b: ".",
      strategic_partners: "الشركاء الاستراتيجيون",
      sp1: "<b>BMV Global</b> — التوزيع الحصري لمنظومة مؤشر UCS",
      sp2: "<b>B3</b> — سجل المُصدِرين الرسمي · Resolução nº 31/2022",
      sp3_a: "<b>وزارة الطاقة · KSA</b>",
      sp3_note: "— قيد المناقشة",
      sp3_b: " · مواءمة رؤية 2030 للحياد الكربوني",
      sp4: "<b>شبكة التدقيق</b> — SGS · TÜV Rheinland · EY · ERM",
      platform_traction: "تبنّي المنصّة",
      t1_a: "قيد الإنتاج ·",
      t1_url: "credit-marketplace-11.emergent.host",
      t2_stats: "{{count}} مشاريع · {{hectares}} هكتار · {{tons}} طن CO₂",
      t3: "مسارات تسوية Stripe + SWIFT + USDT مباشرة",
      t4: "5 لغات · 5 عملات · KYB متعدد الاختصاصات",
      t5: "بريد Resend المعاملاتي نشط",
    },
    s10: {
      eyebrow: "الطلب",
      h1_a: "نجمع",
      h1_em: "3 ملايين يورو",
      h1_b: "تمويل تأسيسي.",
      sub: "لتحويل منصّة تعمل بالكامل إلى بورصة تلقائية لسوق الكربون الطوعي البرازيلي المنظَّم.",
      a1_t: "الدخول إلى السوق",
      a1_s: "مبيعات المؤسسات · شراكات مشتريات ESG في KSA / الاتحاد الأوروبي / المملكة المتحدة",
      a2_t: "الهندسة والامتثال",
      a2_s: "FATF Travel Rule · تحضير MiCA · التسوية على السلسلة",
      a3_t: "التنظيم والاستراتيجية",
      a3_s: "اتفاقيات شراء سيادية · حصريات مع شركات التدقيق",
      contact: "investors@greenbelt.io ←",
      demo_cta: "افتح العرض المباشر ←",
    },
  },
};

export default pitch;
